/**
 * Compact Conversation Simulator component
 *
 * This component provides a simplified call simulation experience for testing.
 * It allows simulating conversations without complex LiveGuidance dependencies.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

// Define a type for the botSocket that matches what's passed from CallDisplay
type BotSocketType = {
  send: (message: string) => void;
  close: () => void;
  reconnect: () => void;
};

interface CompactConversationSimulatorProps {
  botSocket: BotSocketType;
}

interface CustomerProfile {
  name: string;
  company: string;
  role: string;
  problem: string;
  budget: string;
  timeline: string;
  interests: string;
}

/**
 * CompactConversationSimulator provides a simplified interface for
 * testing the LiveGuidance feature with automatic greeting generation
 */
export function CompactConversationSimulator({
  botSocket,
}: Readonly<CompactConversationSimulatorProps>) {
  const [isLoading, setIsLoading] = useState(false);
  const [persona, setPersona] = useState<
    "executive" | "technical" | "skeptical" | "enthusiastic"
  >("executive");
  const [customerResponse, setCustomerResponse] = useState("");
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ speaker: string; text: string }>
  >([]);
  const [sdrName, setSdrName] = useState("Stef");
  const [companyName, setCompanyName] = useState("Frontier AI");

  // Simple sample dialogue for testing
  const sampleDialogue = [
    { speaker: "sdr", text: "Hi, thanks for joining the call today!" },
    {
      speaker: "customer",
      text: "Hello, yes I'm interested to learn more about your solution.",
    },
    {
      speaker: "sdr",
      text: "Great! Can you tell me a bit about your current challenges?",
    },
    {
      speaker: "customer",
      text: "We're struggling with manual processes that take too much time.",
    },
    {
      speaker: "sdr",
      text: "I understand. Our solution can help automate those processes. What's your timeline for implementing a solution?",
    },
  ];
  const [transcriptIndex, setTranscriptIndex] = useState(0);

  // Predefined customer personas
  const personas: Record<
    "executive" | "technical" | "skeptical" | "enthusiastic",
    CustomerProfile
  > = {
    executive: {
      name: "Alex Johnson",
      company: "TechInnovate",
      role: "Director of Sales",
      problem: "Sales team spending too much time on administrative tasks",
      budget: "$50,000 per year",
      timeline: "Needs a solution in the next 3 months",
      interests: "Improving sales efficiency, automation, and ROI metrics",
    },
    technical: {
      name: "Sam Chen",
      company: "DataTech Systems",
      role: "CTO",
      problem: "Integration with existing systems and scalability concerns",
      budget: "$75,000 for implementation",
      timeline: "Looking to implement before end of fiscal year",
      interests:
        "Technical specifications, API documentation, security features",
    },
    skeptical: {
      name: "Jamie Williams",
      company: "Consolidated Services",
      role: "Procurement Manager",
      problem: "Previous vendor solutions failed to deliver promised results",
      budget: "Limited - needs strong ROI justification",
      timeline: "No urgent timeline, cautious approach",
      interests: "Proof points, case studies, guarantees, and references",
    },
    enthusiastic: {
      name: "Taylor Rodriguez",
      company: "GrowthStart",
      role: "CEO",
      problem: "Need to scale quickly with limited resources",
      budget: "Flexible for the right solution",
      timeline: "ASAP - experiencing rapid growth",
      interests:
        "Quick implementation, future roadmap, partnership opportunities",
    },
  };

  // Get a simple customer response based on persona
  const getCustomerResponse = (persona: string): string => {
    const responses = {
      executive: "That sounds interesting. What kind of ROI can we expect?",
      technical: "How does this integrate with our existing systems?",
      skeptical: "I've heard similar promises before. Can you show me proof?",
      enthusiastic:
        "That's exactly what we need! How quickly can we get started?",
    };
    return (
      responses[persona as keyof typeof responses] || "Could you tell me more?"
    );
  };

  // Simple greeting generation
  const generateGreeting = (): string => {
    return `Hi, I'm ${sdrName} from ${companyName}. Thanks for joining the call today! I'm excited to learn more about your needs and see how we might be able to help.`;
  };

  // Start a new call simulation
  const startNewCall = () => {
    setIsLoading(true);

    // Reset conversation history
    setConversationHistory([]);

    // Generate and send greeting
    const greeting = generateGreeting();
    sendTranscriptUpdate("host", greeting);

    // Auto-generate customer response after a delay
    setTimeout(() => {
      const selectedPersona = personas[persona as keyof typeof personas];
      const customerGreeting = `Hi, I'm ${selectedPersona.name} from ${selectedPersona.company}. I received your email and wanted to learn more about your solution.`;
      sendTranscriptUpdate("guest", customerGreeting);
    }, 1000);

    setIsLoading(false);
  };

  // Send transcript update to the bot socket
  const sendTranscriptUpdate = (speaker: "host" | "guest", text: string) => {
    const time = Date.now() / 1000;

    // Send to the actual bot socket
    botSocket.send(
      JSON.stringify({
        type: "SIMULATE_TRANSCRIPT",
        payload: { mode: speaker, text, time },
        timestamp: new Date().toISOString(),
      }),
    );

    // Add to our internal conversation history
    setConversationHistory((prev) => [
      ...prev,
      {
        speaker: speaker === "host" ? "sdr" : "customer",
        text,
      },
    ]);
  };

  // Generate and send a customer response
  const generateCustomerResponse = () => {
    const response = getCustomerResponse(persona);
    setCustomerResponse(response);
  };

  // Handle sending a scripted conversation turn
  const handleScriptedMessage = () => {
    if (transcriptIndex >= sampleDialogue.length) {
      setTranscriptIndex(0);
      return;
    }

    const message = sampleDialogue[transcriptIndex];
    sendTranscriptUpdate(
      message.speaker === "sdr" ? "host" : "guest",
      message.text,
    );

    setTranscriptIndex(transcriptIndex + 1);
  };

  // Send the generated customer response
  const handleSendCustomerResponse = () => {
    if (!customerResponse) return;

    sendTranscriptUpdate("guest", customerResponse);
    setCustomerResponse("");
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Call Setup</h3>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="your-name" className="text-xs text-gray-700">
              Your Name
            </label>
            <input
              id="your-name"
              type="text"
              value={sdrName}
              onChange={(e) => setSdrName(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label htmlFor="your-company" className="text-xs text-gray-700">
              Your Company
            </label>
            <input
              id="your-name"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="customer-type" className="text-xs text-gray-700">
            Customer Type
          </label>
          <Select
            value={persona}
            onValueChange={(
              value: "executive" | "technical" | "skeptical" | "enthusiastic",
            ) => setPersona(value)}
          >
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Select customer persona" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="executive">Executive Buyer</SelectItem>
              <SelectItem value="technical">Technical Evaluator</SelectItem>
              <SelectItem value="skeptical">Skeptical Prospect</SelectItem>
              <SelectItem value="enthusiastic">Enthusiastic Starter</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          size="sm"
          variant="default"
          onClick={startNewCall}
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? "Starting..." : "Start New Call"}
        </Button>
      </div>

      {conversationHistory.length > 0 && (
        <div className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-medium">Conversation History</h3>
          <ScrollArea className="max-h-[150px] space-y-2 overflow-y-auto rounded border p-2">
            {conversationHistory.map((item, index) => (
              <div
                key={`item-${index}`}
                className={`rounded p-1.5 text-sm ${
                  item.speaker === "sdr" ? "ml-4 bg-blue-50" : "mr-4 bg-gray-50"
                }`}
              >
                <div className="text-xs font-medium">
                  {item.speaker === "sdr"
                    ? sdrName
                    : personas[persona as keyof typeof personas].name}
                </div>
                <div>{item.text}</div>
              </div>
            ))}
            {isLoading && (
              <div className="animate-pulse py-1 text-center text-xs text-gray-500">
                Generating response...
              </div>
            )}
          </ScrollArea>

          <div className="text-center text-xs text-gray-500">
            Use the buttons below to continue the conversation
          </div>
        </div>
      )}

      <div className="space-y-2 border-t pt-4">
        <h3 className="text-sm font-medium">Simulation Options</h3>
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleScriptedMessage}
            disabled={isLoading}
          >
            Send Scripted Line
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setTranscriptIndex(0)}
            disabled={isLoading}
          >
            Reset Script
          </Button>
        </div>

        <div className="space-y-2">
          <Button
            size="sm"
            variant="outline"
            onClick={generateCustomerResponse}
            disabled={isLoading || conversationHistory.length === 0}
            className="w-full"
          >
            Generate Customer Response
          </Button>

          {customerResponse && (
            <div className="space-y-2">
              <Textarea
                value={customerResponse}
                onChange={(e) => setCustomerResponse(e.target.value)}
                rows={2}
                className="resize-none text-sm"
                placeholder="Customer response will appear here..."
              />
              <Button
                size="sm"
                variant="default"
                onClick={handleSendCustomerResponse}
                className="w-full"
                disabled={isLoading}
              >
                Send Customer Response
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
